// What the platform audit log is filtered by (spec/07 §11.2): the organisation's filters — actor,
// action (exact, or `prefix.*`), target, venue, break-glass grant, request id, a window of two
// instants — plus an organisation. The screen keeps them as the strings it sends; the server checks
// each again and names the one it refuses (`400`).
//
// **The window is UTC.** The log spans organisations whose venues may keep different time zones, so
// the screen asks for the two instants themselves, in UTC, half-open as the api reads them — from
// the first, and before the second. No wall clock has to be turned into an instant here. The quick
// ranges (the last 24 hours, 7 days, 30 days) are instants too: now, less the range, and no end.
//
// The filter bar's one search box reads what is pasted into it: a request id traces that request,
// an organisation's or a venue's id filters by it, and anything else is taken as an action — its
// copy ("Shift closed"), its code (`shifts.shift.closed`), or a module (`shifts`, read as
// `shifts.*`).

import type { ListPlatformAuditQuery } from '@clavian/api-client';
import { isTypeId } from '@clavian/contracts/typeid';

export const AUDIT_FILTER_KEYS = [
  'organizationId',
  'venueId',
  'action',
  'actorKind',
  'actorId',
  'targetType',
  'targetId',
  'onBehalfOf',
  'requestId',
  'from',
  'to',
] as const;

export type AuditFilterKey = (typeof AUDIT_FILTER_KEYS)[number];

/** A filter as applied: only the keys that were given, each a string the api takes. */
export type AuditFilter = Readonly<Partial<Record<AuditFilterKey, string>>>;

/** `AuditActorSchema.kind`. */
export const AUDIT_ACTOR_KINDS = ['user', 'member', 'device', 'api_key', 'system'] as const;

/** Why a reader is reading: `PlatformAuditListQuery.reason`, trimmed. */
export const REASON_MIN = 3;
export const REASON_MAX = 500;

/** The most rows one export carries (`AUDIT_EXPORT_MAX_ROWS`): past it the api refuses with `422`. */
export const EXPORT_MAX_ROWS = 10_000;

/** The filter as the list's query (and the export's body) take it. */
export function auditQuery(filter: AuditFilter): Omit<ListPlatformAuditQuery, 'reason' | 'cursor' | 'limit'> {
  return filter as Omit<ListPlatformAuditQuery, 'reason' | 'cursor' | 'limit'>;
}

/** A request id (`req_…`), which is also its trace id (spec/08 §2.11). */
export const isRequestId = (value: string): boolean => isTypeId(value, 'req');

/** The form's `datetime-local` value, read as UTC, as the instant the api takes. */
export function instantFromUtcInput(value: string): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(value)) return undefined;
  const instant = new Date(`${value.length === 16 ? `${value}:00` : value}.000Z`);
  return Number.isNaN(instant.getTime()) ? undefined : instant.toISOString();
}

/** An instant, back into the form's `datetime-local` value, in UTC. */
export const utcInputFromInstant = (instant: string): string => instant.slice(0, 16);

export type FilterForm = Readonly<Record<AuditFilterKey, string>>;

export const EMPTY_FORM: FilterForm = Object.fromEntries(AUDIT_FILTER_KEYS.map((key) => [key, ''])) as FilterForm;

export function formFromFilter(filter: AuditFilter): FilterForm {
  return {
    ...EMPTY_FORM,
    ...filter,
    from: filter.from === undefined ? '' : utcInputFromInstant(filter.from),
    to: filter.to === undefined ? '' : utcInputFromInstant(filter.to),
  };
}

export type FilterProblem =
  | 'organization'
  | 'venue'
  | 'from'
  | 'to'
  | 'window';

/** The form, checked for what is plainly not an id or an instant, and made the filter it asks for. */
export function filterFromForm(form: FilterForm): { filter: AuditFilter; problems: Partial<Record<AuditFilterKey, FilterProblem>> } {
  const problems: Partial<Record<AuditFilterKey, FilterProblem>> = {};
  const filter: Partial<Record<AuditFilterKey, string>> = {};
  for (const key of AUDIT_FILTER_KEYS) {
    const value = form[key].trim();
    if (value === '') continue;
    if (key === 'from' || key === 'to') {
      const instant = instantFromUtcInput(value);
      if (instant === undefined) problems[key] = key;
      else filter[key] = instant;
      continue;
    }
    filter[key] = key === 'organizationId' || key === 'venueId' || key === 'requestId' ? value.toLowerCase() : value;
  }
  if (filter.organizationId !== undefined && !isTypeId(filter.organizationId, 'org')) problems.organizationId = 'organization';
  if (filter.venueId !== undefined && !isTypeId(filter.venueId, 'ven')) problems.venueId = 'venue';
  if (filter.from !== undefined && filter.to !== undefined && Date.parse(filter.from) >= Date.parse(filter.to)) problems.to = 'window';
  return { filter, problems };
}

/** `attachment; filename="audit-platform-2026-09-22.csv"` → the name, or `undefined`. */
export function fileNameOf(disposition: string | null): string | undefined {
  const name = disposition === null ? undefined : /filename="([^"/\\]+)"/.exec(disposition)?.[1];
  return name === undefined || name === '' ? undefined : name;
}

/** What the api would call the file, for when its header cannot be read (another origin that hides it). */
export const fallbackFileName = (filter: AuditFilter, at: Date = new Date()): string =>
  `audit-${filter.organizationId ?? 'platform'}-${at.toISOString().slice(0, 10)}.csv`;

/** The quick time ranges, and how far back each reaches. `all` sends no window; `custom` is the form's. */
export const AUDIT_RANGES = ['day', 'week', 'month', 'all', 'custom'] as const;
export type AuditRange = (typeof AUDIT_RANGES)[number];

const RANGE_MS: Readonly<Record<'day' | 'week' | 'month', number>> = {
  day: 24 * 60 * 60 * 1000,
  week: 7 * 24 * 60 * 60 * 1000,
  month: 30 * 24 * 60 * 60 * 1000,
};

/** The filter with a quick range in place of any window it had. */
export function withRange(filter: AuditFilter, range: AuditRange, now: number = Date.now()): AuditFilter {
  const rest = without(filter, 'from', 'to');
  if (range === 'all' || range === 'custom') return rest;
  return { ...rest, from: new Date(now - RANGE_MS[range]).toISOString() };
}

/** The filter without `keys`. */
export function without(filter: AuditFilter, ...keys: readonly AuditFilterKey[]): AuditFilter {
  return Object.fromEntries(Object.entries(filter).filter(([key]) => !keys.includes(key as AuditFilterKey))) as AuditFilter;
}

/** The fields "More filters" holds, for its count. */
export const MORE_FILTER_KEYS = ['organizationId', 'venueId', 'action', 'actorId', 'targetType', 'targetId', 'onBehalfOf', 'from', 'to'] as const;

/** `PlatformAuditListQuery.action`: an exact action, or a prefix written `sessions.*`. */
const ACTION_FILTER = /^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)*(?:\.\*)?$/;

export type SearchTerm =
  | { readonly key: 'requestId' | 'organizationId' | 'venueId' | 'action'; readonly value: string }
  | undefined;

/**
 * What the search box asks for, or `undefined` for words it cannot read. `actionsByLabel` maps an
 * action's copy, lower-cased, to its code.
 */
export function parseSearch(text: string, actionsByLabel: ReadonlyMap<string, string> = new Map()): SearchTerm {
  const value = text.trim().toLowerCase();
  if (value === '') return undefined;
  if (isRequestId(value)) return { key: 'requestId', value };
  if (isTypeId(value, 'org')) return { key: 'organizationId', value };
  if (isTypeId(value, 'ven')) return { key: 'venueId', value };
  const labelled = actionsByLabel.get(value.replace(/\s+/g, ' '));
  if (labelled !== undefined) return { key: 'action', value: labelled };
  if (!ACTION_FILTER.test(value)) return undefined;
  // One word is a module: every action under it.
  return { key: 'action', value: value.includes('.') ? value : `${value}.*` };
}

/**
 * An id, short enough for a table cell: a TypeID keeps its prefix and its last six characters
 * (`org_…pf2dz`), a uuid its first eight. The full id is always one click away.
 */
export function shortId(id: string): string {
  const typeId = /^([a-z][a-z_]*)_([0-9a-z]{26})$/.exec(id);
  if (typeId !== null) return `${typeId[1]}_…${(typeId[2] ?? '').slice(-6)}`;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(id)) return id.slice(0, 8);
  return id.length > 14 ? `${id.slice(0, 12)}…` : id;
}
